// Apolo IDE — CodeIdePanel composes Editor + FileTree + ModifiedFilesSidebar.

import { useCallback, useEffect, useState } from 'react';
import { Code2, Save } from 'lucide-react';
import FileTree from './FileTree.jsx';
import Editor from './Editor.jsx';
import GhostToggle from './GhostToggle.jsx';
import ModifiedFilesSidebar from './ModifiedFilesSidebar.jsx';
import { ELECTRON_REQUIRED_MESSAGE, hasElectronBridge } from '../../lib/electronBridge.js';
import { useApoloRulesAutoLoad } from '../../hooks/useApoloRulesAutoLoad.js';

export default function CodeIdePanel({ session }) {
  const root = session?.directory || '';
  const [openFile, setOpenFile] = useState(null);
  const [content, setContent] = useState('');
  const [mtime, setMtime] = useState(null);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  // Sprint v11 E4 — auto-load .apolorules from worktree root when the
  // session opens. Silent if the file is absent.
  useApoloRulesAutoLoad(session?.id, root);

  const handleOpen = useCallback(
    async (entry) => {
      if (!hasElectronBridge()) {
        setError(ELECTRON_REQUIRED_MESSAGE);
        return;
      }
      if (entry.isDirectory) return;
      try {
        setError(null);
        const { content: text, mtime: mt } = await window.apolo.file.read({ root, filePath: entry.path });
        setOpenFile(entry);
        setContent(text);
        setMtime(mt);
      } catch (e) {
        setError(e?.message || 'Falha ao abrir arquivo');
      }
    },
    [root],
  );

  const handleSave = useCallback(
    async (newText) => {
      if (!openFile || !hasElectronBridge()) return;
      try {
        const res = await window.apolo.file.write({
          root,
          filePath: openFile.path,
          content: newText,
          expectedMtime: mtime,
        });
        setMtime(res.mtime);
        setSavedAt(new Date().toISOString());
      } catch (e) {
        setError(e?.message || 'Falha ao salvar');
      }
    },
    [openFile, root, mtime],
  );

  useEffect(() => {
    // Drop the open file when we switch sessions.
    setOpenFile(null);
    setContent('');
    setMtime(null);
    setError(null);
    setSavedAt(null);
  }, [session?.id]);

  return (
    <div className="workbench-code-editor flex h-full min-w-0 flex-1">
      <aside aria-label="Arquivos" className="flex w-64 shrink-0 flex-col border-r border-apolo-borderSubtle bg-apolo-surface1">
        <FileTree root={root} onOpenFile={handleOpen} />
      </aside>
      <main className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-apolo-borderSubtle bg-apolo-surface1 px-3 py-2">
          <div className="min-w-0 truncate text-xs text-apolo-textTertiary">
            {openFile ? <span className="text-apolo-textPrimary">{openFile.path}</span> : 'Nenhum arquivo aberto'}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <GhostToggle />
            {savedAt && (
              <span className="inline-flex items-center gap-1 text-[11px] text-apolo-success">
                <Save size={10} aria-hidden="true" />
                salvo {new Date(savedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            )}
          </div>
        </header>
        {error && (
          <div role="alert" className="border-b border-apolo-error/30 bg-apolo-error/10 px-3 py-1 text-xs text-apolo-error">
            {error}
          </div>
        )}
        <div className="flex-1 overflow-hidden p-2">
          {openFile ? (
            <Editor
              filename={openFile.name}
              filePath={openFile.path}
              value={content}
              onChange={setContent}
              onSave={handleSave}
              readOnly={!hasElectronBridge()}
              sessionId={session?.id}
              modelId={session?.model}
            />
          ) : (
            <div className="workbench-editor-empty flex h-full flex-col items-center justify-center gap-3 p-6 text-center text-xs text-apolo-textTertiary">
              <Code2 size={32} strokeWidth={1.2} />
              <strong>Seu código, em foco.</strong>
              <span>Escolha um arquivo para começar a editar.</span>
            </div>
          )}
        </div>
      </main>
      <ModifiedFilesSidebar root={root} revision={savedAt} onOpenFile={handleOpen} />
    </div>
  );
}
